import * as React from 'react';
import { st, classes } from './RatingsInput.st.css';
import { Favorite, FavoriteFill } from '@wix/wix-ui-icons-common/on-stage';
import { FavoriteFillLarge, FavoriteLarge, } from '@wix/wix-ui-icons-common/on-stage/system';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import { RatingsInputDataHooks, RatingsInputDataKeys } from './dataHooks';
import uniqueId from '../../common/uniqueId';
/** Rating input is used to collect measurable feedback from users. */
export const RatingsInput = React.forwardRef(({ className, value, disabled, onSelect = () => { }, label, size = 'small', labelLayout = 'labelTop', errorMessage, errorAppearance, getReactionLabel, showReactionLabel = true, ...props }, ref) => {
    const [hoveredRating, setHoveredRating] = React.useState();
    const errorMessageId = React.useMemo(() => uniqueId('error-message-id'), []);
    const ratingName = React.useMemo(() => uniqueId('rating-name'), []);
    const ratingItems = React.useRef(new Map());
    React.useImperativeHandle(ref, () => ({
        focus: () => {
            ratingItems.current.get(1).focus();
        },
        blur: () => {
            currentRating && ratingItems.current.get(currentRating).blur();
        },
    }));
    React.useEffect(() => {
        if (typeof value === 'undefined' &&
            typeof hoveredRating !== 'undefined') {
            setHoveredRating(undefined);
        }
    }, [value]);
    const FilledStar = size === 'small' ? FavoriteFill : FavoriteFillLarge;
    const EmptyStar = size === 'small' ? Favorite : FavoriteLarge;
    const currentRating = hoveredRating ? hoveredRating : value;
    const setRatingRef = (ratingRef, key) => {
        if (!ratingItems.current.has(key)) {
            ratingItems.current.set(key, ratingRef);
        }
    };
    return (React.createElement(ErrorMessageWrapper, { 'data-hook': props['data-hook'],
        visible: Boolean(errorMessage),
        message: errorMessage,
        errorAppearance,
        messageId: errorMessageId,
        className: st(classes.root, {
            layout: labelLayout,
            size,
            disabled,
            withError: Boolean(errorMessage),
        }, className) },
        React.createElement("fieldset", { className: classes.container, "data-hook": RatingsInputDataHooks.Container, [RatingsInputDataKeys.Size]: size,
            [RatingsInputDataKeys.Disabled]: Boolean(disabled),
            [RatingsInputDataKeys.Layout]: labelLayout },
            label ? (React.createElement("legend", { className: classes.label, "data-hook": RatingsInputDataHooks.Label }, label)) : null,
            React.createElement("div", { className: classes.ratingsWrapper }, [1, 2, 3, 4, 5].map((rating) => {
                const ratingId = `${ratingName}-rating-${rating}`;
                const isActive = rating <= currentRating;
                return (React.createElement("div", { key: rating, "data-hook": RatingsInputDataHooks.RatingStarWrapper, className: classes.ratingItem, onMouseEnter: () => !disabled && setHoveredRating(rating), onMouseLeave: () => !disabled && setHoveredRating(undefined), onMouseDown: () => !disabled && onSelect(rating) },
                    React.createElement("input", { "data-hook": RatingsInputDataHooks.RatingStar, [RatingsInputDataKeys.Active]: isActive, id: ratingId, ref: (ratingRef) => setRatingRef(ratingRef, rating), className: classes.visuallyHidden, value: rating, type: "radio", checked: rating === value, name: ratingName, disabled: disabled, onChange: () => !disabled && onSelect(rating), "aria-describedby": errorMessage && errorMessageId, "aria-label": getReactionLabel?.(rating) }),
                    React.createElement("span", { className: classes.icon, "aria-hidden": true }, isActive ? React.createElement(FilledStar, null) : React.createElement(EmptyStar, null))));
            })),
            showReactionLabel && getReactionLabel && currentRating ? (React.createElement("span", { className: st(classes.ratingLabel, {
                    size,
                }), "aria-hidden": true, "data-hook": RatingsInputDataHooks.ReactionLabel }, getReactionLabel(currentRating))) : null)));
});
RatingsInput.displayName = 'RatingsInput';
//# sourceMappingURL=RatingsInput.js.map